import { useState } from 'react'
import {Plus, Minus} from 'lucide-react'

const FAQS = [
  {
    q: 'How do I shred fat and build muscle at the same time?',
    a: 'This is called body recomposition, and it works best for beginners, those returning from a layoff, or anyone with higher body fat. The partition rule is simple: keep protein very high (around 1.1g per pound of lean mass), run only a modest deficit, and train with progressive resistance. Your body pulls energy from fat stores to fuel muscle repair — letting you shred muscle fat while slowly building. Advanced lifters recomp slower, which is why a structured shredding and bulking cycle eventually becomes more efficient.',
  },
  {
    q: 'What is the best way to get a ripped body?',
    a: 'The best way to get a ripped body is a consistent caloric deficit combined with hard resistance training — there is no shortcut around energy balance. Set your deficit between 15% and 25%, hit your protein target every single day, lift heavy 3-5 times per week using metabolic resistance training, and stay patient. Cardio accelerates the process but the deficit is what reveals the shredded lean body underneath.',
  },
  {
    q: 'How much body fat is required to be ripped?',
    a: 'For men, a ripped, visibly shredded look typically appears in the single digits — roughly 6-9% body fat where abs, vascularity, and muscle separation are obvious. For women, the equivalent lean and ripped body shows around 16-20% body fat due to essential fat differences. A bodybuilder shredded for competition may briefly dip even lower, but those extremes are not sustainable or healthy year-round.',
  },
  {
    q: 'Why does our Shred Calculator use Katch-McArdle?',
    a: 'When you provide your body fat percentage, we switch to the Katch-McArdle formula because it calculates your BMR from lean body mass rather than total weight. This matters enormously during a shred: it prevents under-eating that triggers muscle wasting, and it scales protein and calories to the tissue you actually want to keep. The result is a lean ripped body that retains strength — the entire point of intelligent shredding in bodybuilding rather than crash dieting.',
  },
]

function Faq() {
  const [open, setOpen] = useState<number | null>(0)

  return (
    <section id="faq" className="relative py-20 lg:py-28 scroll-mt-20 bg-white/[0.012] border-t border-white/[0.05]">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <header className="text-center">
          <span className="font-mono text-[13px] uppercase tracking-[0.2em] text-blue-500">
            FAQ
          </span>
          <h2 className="font-display mt-3 text-[34px] sm:text-[42px] font-bold tracking-[-0.02em] text-white">
            Get ripped — your questions answered
          </h2>
        </header>

        <div className="mt-12 space-y-3">
          {FAQS.map((f, i) => {
            const isOpen = open === i
            return (
              <div
                key={f.q}
                className={`rounded-2xl border transition-colors ${
                  isOpen
                    ? 'border-blue-500/30 bg-blue-500/[0.04]'
                    : 'border-white/[0.07] bg-white/[0.02]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                  aria-expanded={isOpen}
                >
                  <span className="font-display text-[17px] font-semibold text-white">{f.q}</span>
                  <span
                    className={`grid place-items-center w-8 h-8 shrink-0 rounded-full border transition-colors ${
                      isOpen ? 'border-blue-500/40 text-blue-400' : 'border-white/15 text-slate-400'
                    }`}
                  >
                    {isOpen ? (
                      <Minus className="w-4 h-4" aria-hidden="true" />
                    ) : (
                      <Plus className="w-4 h-4" aria-hidden="true" />
                    )}
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-300 ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-6 text-[15px] leading-relaxed text-slate-400">{f.a}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default Faq
